"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { timings } from '../../../constants/timings.js';
import { isIOS } from '../../helpers/platform.js';
import { Box } from '../../../Box/Box.web.js';
import { colors } from '../../../constants/colors.js';
import { opacify, setTransitionProps } from '../../helpers/style.js';
import styled from 'styled-components';
import { callOptional } from '../../helpers/function.js';

var _excluded = ["centered", "children", "opened", "onClose", "onMountChange", "backgroundColor"];
// Use -webkit-fill-available primarly to solve IOS safari bottombar issue when using 100vh
var StyledOverlay = /*#__PURE__*/styled(Box).withConfig({
  displayName: "Overlayweb__StyledOverlay",
  componentId: "sc-2zyecy-0"
})(["position:fixed;top:0;left:0;height:100vh;height:-webkit-fill-available;width:100vw;z-index:1000;overflow:auto;"]);
var Overlay = function Overlay(_ref) {
  var _ref$centered = _ref.centered,
    centered = _ref$centered === undefined ? false : _ref$centered,
    children = _ref.children,
    opened = _ref.opened,
    onClose = _ref.onClose,
    onMountChange = _ref.onMountChange,
    backgroundColor = _ref.backgroundColor,
    props = _objectWithoutProperties(_ref, _excluded);
  var unmountTimeout = React.useRef();
  var mountTimeout = React.useRef();
  var iosTimeout = React.useRef();
  var iosResizeHandler = React.useRef();
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    mounted = _React$useState2[0],
    setMounted = _React$useState2[1];
  var ref = /*#__PURE__*/React.createRef();
  var clearTimeouts = function clearTimeouts() {
    clearTimeout(mountTimeout.current);
    clearTimeout(unmountTimeout.current);
  };
  var handleKeydown = React.useCallback(function (e) {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  React.useEffect(function () {
    if (opened) {
      clearTimeouts();
      var scrollbarWidth = window.innerWidth - document.body.clientWidth;
      document.addEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = "".concat(scrollbarWidth, "px");
      mountTimeout.current = window.setTimeout(function () {
        setMounted(true);
        callOptional(onMountChange)(true);
      }, 0);
    }
    return function () {
      if (opened) {
        clearTimeouts();
        document.removeEventListener('keydown', handleKeydown);
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.paddingRight = '';
        unmountTimeout.current = window.setTimeout(function () {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          // TODO: this is a bug, don't set state after unmount
          setMounted(false);
          callOptional(onMountChange)(false);
        }, timings.slowest);
      }
    };
  }, [opened, handleKeydown, onMountChange]);
  React.useEffect(function () {
    if (!isIOS()) {
      return;
    }
    if (opened && mounted) {
      clearTimeout(iosTimeout.current);
      var currentDiv = ref.current;
      // istanbul ignore next
      if (currentDiv) {
        iosResizeHandler.current = function () {
          currentDiv.style.height = "".concat(window.innerHeight, "px");
        };
        window.addEventListener('resize', iosResizeHandler.current);
      }
      iosTimeout.current = window.setTimeout(function () {
        var _window = window,
          scrollY = _window.scrollY,
          scrollX = _window.scrollX,
          innerHeight = _window.innerHeight;
        document.body.style.position = 'fixed';
        document.body.style.top = "".concat(-scrollY, "px");
        document.body.style.left = "".concat(-scrollX, "px");
        document.body.style.height = "".concat(innerHeight, "px");
        document.documentElement.style.height = '100vh';
        iosTimeout.current = undefined;
      }, timings.slowest);
      return function () {
        clearTimeout(iosTimeout.current);
        if (iosResizeHandler.current) {
          window.removeEventListener('resize', iosResizeHandler.current);
        }
        var y = -parseInt(document.body.style.top, 10);
        var x = -parseInt(document.body.style.left, 10);

        // Restore styles
        window.scrollTo(x, y);
        document.body.style.position = '';
        document.body.style.height = '';
        document.documentElement.style.height = '';
      };
    }
  }, [opened, mounted, ref, setMounted]);
  React.useEffect(function () {
    var _ref$current;
    if (!(((_ref$current = ref.current) === null || _ref$current === undefined ? undefined : _ref$current.parentElement) === document.body) || !opened) {
      return;
    }
    var elementsToHide = Array.from(document.body.children).filter(function (node) {
      return node !== ref.current && node.tagName !== 'SCRIPT';
    });
    var storedValues = elementsToHide.map(function (node) {
      return {
        ariaHidden: node.getAttribute('aria-hidden'),
        inert: node.getAttribute('inert')
      };
    });
    elementsToHide.forEach(function (node) {
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    });
    return function () {
      elementsToHide.forEach(function (node, i) {
        var storedValue = storedValues[i];
        if (storedValue.ariaHidden) {
          node.setAttribute('aria-hidden', storedValue.ariaHidden);
        } else {
          node.removeAttribute('aria-hidden');
        }
        if (storedValue.inert) {
          node.setAttribute('inert', storedValue.inert);
        } else {
          node.removeAttribute('inert');
        }
      });
    };
  }, [opened, ref]);
  var handleOverlayClick = React.useCallback(function (e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    opacity = _React$useState4[0],
    setOpacity = _React$useState4[1];
  React.useLayoutEffect(function () {
    if (opened) {
      setOpacity(1);
    } else {
      setOpacity(0);
    }
  }, [opened]);
  var centeredProps = {
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center'
  };
  var background = backgroundColor ? backgroundColor : opacify(colors.fullBlack, 0.6);
  if (opened || mounted) {
    return /*#__PURE__*/React.createElement(StyledOverlay, _extends({
      boxRef: ref,
      "data-testid": "elemental-Overlay",
      "aria-hidden": "false",
      onClick: handleOverlayClick,
      opacity: opacity,
      backgroundColor: background
    }, centered ? centeredProps : {}, setTransitionProps({
      opacity: timings.slowest
    }), props), children);
  }
  return null;
};

export { Overlay };
